import JsBarcode from 'jsbarcode';
import React, { FC, memo, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { PresenterProps } from 'src/components/PickingListComponent';
import { PickingList, ShipperList, UnloadList, ItemList } from 'src/entities/pickingListEntity';
import { TruckEntity } from 'src/entities/truckEntity';

import style from './print.module.css';

function PickingHeader({ unload, course }: { unload: UnloadList; course: PickingList }) {
  const rows: [string, React.ReactNode][] = [
    ['運送会社名', unload.items?.[0]?.item_carrier_company ?? ''],
    ['車番', course.truck.license_plate_value],
    ['最大積載量', `${course.truck.maximum_loading_capacity_kg}kg${course.truck.loading_platform_volume_m3 ? `, ${course.truck.loading_platform_volume_m3}m3` : ''}`],
    ['ドライバー名', course.driver.name],
  ];
  return (
    <>
      <div className={style.picking_title_wrapper}>
        <h1 className={style.picking_title}>ピッキングリスト</h1>
      </div>
      <div className={style.flex_area}>
        <div className={style.info_table}>
          {rows.map(([label, value]) => (
            <div key={String(label)} className={style.info_row}>
              <div className={style.info_label}>{label}</div>
              <div className={style.info_value}>{value}</div>
            </div>
          ))}
        </div>
        <div className={style.signature_area}>
          <div className={style.signature_box}>
            <div className={style.signature_header}>仕分け作業者</div>
            <div className={style.signature_body} />
          </div>
          <div className={style.signature_box}>
            <div className={style.signature_header}>検品者</div>
            <div className={style.signature_dual_body}>
              <div className={style.signature_dual_cell} />
              <div className={style.signature_dual_cell} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className={style.delivery_row}>
      <div className={style.delivery_label}>{label}</div>
      <div className={style.delivery_value}>{value}</div>
    </div>
  );
}

function Barcode({ value }: { value: string }) {
  const ref = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!ref.current || !value) return;

    try {
      JsBarcode(ref.current, value, {
        format: 'CODE128',
        width: 1.6,
        height: 36,
        displayValue: true,
        fontSize: 10,
        margin: 0,
      });
    } catch {
      // バーコード化できない値でも帳票全体の表示は継続する。
    }
  }, [value]);

  return <svg ref={ref} className={style.barcode} data-testid="slip-barcode" />;
}

function getSlipNumbers(unload: UnloadList): string[] {
  return unload.slip_number_list?.length
    ? unload.slip_number_list
    : (unload.slip_numbers ?? '').split(',').map((slipNumber) => slipNumber.trim()).filter(Boolean);
}

function SlipNumberValue({ unload, barcode }: { unload: UnloadList; barcode: boolean }) {
  const slipNumbers = getSlipNumbers(unload);

  if (!barcode) return <>{unload.slip_numbers ?? ''}</>;

  return (
    <div className={style.barcode_list}>
      {slipNumbers.map((slipNumber) => (
        <Barcode value={slipNumber} key={slipNumber} />
      ))}
    </div>
  );
}

function DriverInfo({ unload, shipper, barcode }: { unload: UnloadList; shipper: ShipperList; barcode: boolean }) {
  return (
    <div className={style.delivery_info}>
      <div className={style.delivery_row_grid}>
        <InfoRow label="積込日" value={unload.items?.[0]?.loading_time_range_start ?? ''} />
        <InfoRow label="納品日" value={unload.items?.[0]?.unloading_time_range_start ?? ''} />
      </div>
      <InfoRow label="荷主名" value={shipper.shipper_name} />
      <InfoRow label="納品先名" value={unload.unloading_name} />
      <InfoRow label="納品先住所" value={unload.items?.[0]?.unloading_address ?? ''} />
      <InfoRow label="伝票No" value={<SlipNumberValue unload={unload} barcode={barcode} />} />
    </div>
  );
}

function GoodsTable({ displayItems }: { displayItems: ItemList[] }) {
  return (
    <table className={style.goods_table}>
      <thead>
        <tr className={style.header}>
          <th style={{ textWrap: 'nowrap' }}>商品CD</th>
          <th style={{ textWrap: 'nowrap' }}>商品名</th>
          <th style={{ textWrap: 'nowrap' }}>ロット</th>
          <th style={{ textWrap: 'nowrap' }}>数量</th>
          <th style={{ textWrap: 'nowrap' }}>荷姿</th>
          <th style={{ textWrap: 'nowrap' }}>備考</th>
        </tr>
      </thead>
      <tbody>
        {displayItems.map((item, index) => (
          <tr className={style.goods} key={`${index}-${item.item_code || 'empty'}-${item.empty_id ?? 'item'}`}>
            <td className={style.text_center}>{item.item_code}</td>
            <td className={style.text_left}>{item.item_name}</td>
            <td className={style.item_lot} aria-label="empty" />
            <td className={style.text_center}>{item.item_count !== 0 ? item.item_count : ''}</td>
            <td className={style.text_center}>{item.item_packing_style}</td>
            <td className={style.item_memo} aria-label="empty" />
          </tr>
        ))}
        <tr className={style.summery_row}>
          <td className={style.summery} colSpan={3}>合計</td>
          <td className={style.summery}>
            {displayItems.reduce((prev, current) => prev + current.item_count, 0) !== 0
              ? displayItems.reduce((prev, current) => prev + current.item_count, 0) : ''}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function PickingFooter({ truck, currentPage, totalPage }: { truck: TruckEntity; currentPage: number; totalPage: number }) {
  return (
    <div className={style.footer}>
      <div className={style.dispatch_box}>{truck.sort_order && `コース：${truck.sort_order}`}</div>
      <div className={style.issue}>
        {`${formatDateTime(new Date())} 発行 `}
        {`<${currentPage}/${totalPage}>`}
      </div>
    </div>
  );
}

const MIN_EMPTY_PAGE_ROWS = 10;
const STANDARD_PAGE_ROWS = 20;
const BARCODE_ROW_REDUCTION = 2;

const emptyData: ItemList[] = Array.from(
  { length: 10 },
  (_, i) => ({
    item_carrier_company: '',
    item_code: '',
    item_count: 0,
    item_name: '',
    item_packing_style: '',
    loading_time_range_start: '',
    unloading_address: '',
    unloading_time_range_start: '',
    empty_id: i
  })
);

function formatDateTime(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}/${m}/${d} ${hh}:${mm}`;
}

export function buildDisplayItems(items: ItemList[], barcodeCount: number): ItemList[] {
  const displayRowCount = items.length === 0
    ? MIN_EMPTY_PAGE_ROWS
    : Math.max(items.length, STANDARD_PAGE_ROWS - barcodeCount * BARCODE_ROW_REDUCTION);

  const emptyRows: ItemList[] = Array.from(
    { length: displayRowCount - items.length },
    (_, i) => ({
      item_carrier_company: '',
      item_code: '',
      item_count: 0,
      item_name: '',
      item_packing_style: '',
      loading_time_range_start: '',
      unloading_address: '',
      unloading_time_range_start: '',
      empty_id: i
    })
  );

  return [...items, ...emptyRows];
}

const PickingListPresenter: FC<PresenterProps> = memo(({ models, date: _date, unit: _unit, barcodeShippers }) => {
  let pageCountCurrent = 0;
  const barcodeShipperSet = new Set(barcodeShippers);

  const pageCountTotal = models.reduce((total, course) => {
    // データが空の場合も1ページ分追加
    if (course.dispatches.length === 0) {
      return total + 1;
    }

    // データがある場合は、荷主・納品先毎にページを追加
    const courseCount = course.dispatches.reduce((dispatchTotal, loadingDate) => {
      const dateCount = loadingDate.shippers.reduce((shipperTotal, shipper) => shipperTotal + shipper.unloads.length, 0);
      return dispatchTotal + dateCount;
    }, 0);
    return total + courseCount;
  }, 0);

  return (
    <>
      <Helmet>
        <title>ピッキングリスト</title>
      </Helmet>
      <div className={style.main}>
        {models.map((course) => (
          <React.Fragment key={`${course.truck.license_plate_value}-${course.driver.name}`}>
            {course.dispatches.length === 0 ? (
              <div className={style.page} style={{ page: 'A4PickingList' }}>
                <PickingHeader unload={{ unloading_name: '', items: [] }} course={course} />
                <DriverInfo unload={{ unloading_name: '', items: [] }} shipper={{ shipper_name: '', unloads: [] }} barcode={false} />
                <GoodsTable displayItems={emptyData} />
                <PickingFooter truck={course.truck} currentPage={++pageCountCurrent} totalPage={pageCountTotal} />
              </div>
            ) : (
              course.dispatches.map((loadDate) => (
                <React.Fragment key={`${course.truck.sort_order}-${loadDate.date}`}>
                  {loadDate.shippers.map((shipper) => (
                    <React.Fragment key={`${course.truck.sort_order}-${loadDate.date}-${shipper.shipper_name}`}>
                      {shipper.unloads.map((unload) => {
                        const barcode = barcodeShipperSet.has(shipper.shipper_name);
                        const barcodeCount = barcode ? getSlipNumbers(unload).length : 0;
                        const displayItems = buildDisplayItems(unload.items, barcodeCount);
                        return (
                          <div className={style.page} style={{ page: 'A4PickingList' }} key={`${course.truck.sort_order}-${loadDate.date}-${shipper.shipper_name}-${unload.unloading_name}`}>
                            <PickingHeader unload={unload} course={course} />
                            <DriverInfo unload={unload} shipper={shipper} barcode={barcode} />
                            <GoodsTable displayItems={displayItems} />
                            <PickingFooter truck={course.truck} currentPage={++pageCountCurrent} totalPage={pageCountTotal} />
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))
            )}
          </React.Fragment>
        ))}
      </div>
    </>
  );
});

export default PickingListPresenter;
